import {CustomerSubscription, SubscriptionItem} from '../types';

// The create and update payloads, WHITELISTED to the billing-api Input types (§6, repo
// CLAUDE.md): every field is named, nothing is spread from the form, and the form's
// values are never mutated.
//
//   input CreateCustomerSubscriptionInput { customerId: ID!, items: [CreateSubscriptionItemInput],
//                                           status, created, createdByUsername }
//   input UpdateCustomerSubscriptionInput { id: ID!, customerId: ID!, items: [UpdateSubscriptionItemInput],
//                                           status, updated, updatedByUsername }
//   input CreateSubscriptionItemInput { subscriptionId, priceId: ID!, quantity: Int!, created, createdByUsername }
//   input UpdateSubscriptionItemInput { id: ID!, subscriptionId: ID!, priceId: ID!, quantity: Int!, updated, updatedByUsername }
//
// FNP-1922 removed only fields those Inputs do not declare, so AppSync stops rejecting
// the request for them: `id` on create, `customer` on update, and the form-only
// `productId` on each item row. Amounts, prices and quantities are passed through exactly
// as before.
//
// ⚠️ `items` keeps the shape it had. When the form still holds the record's item
// CONNECTION ({items, nextToken}) rather than a list of rows, that object is sent as it
// was, which does not match the declared list. Whether Edit should send the item list,
// or leave items to the Items tab, is an open decision on FNP-1922, not a field removal.

type FormItems = CustomerSubscription['items'] | Array<Partial<SubscriptionItem> & { productId?: string }>;

const pick = (row: Record<string, unknown>, keys: string[]) =>
    keys.reduce((acc: Record<string, unknown>, key) => {
        if (typeof row[key] !== 'undefined') {
            acc[key] = row[key];
        }
        return acc;
    }, {});

const CREATE_ITEM_FIELDS = ['subscriptionId', 'priceId', 'quantity'];
const UPDATE_ITEM_FIELDS = ['id', 'subscriptionId', 'priceId', 'quantity'];

const buildItems = (items: FormItems | undefined, subscriptionId: string, itemFields: string[]) => {
    if (Array.isArray(items)) {
        // Rows added in the drawer's item list: keep only the fields the item Input declares.
        return items.map(row => pick(row as Record<string, unknown>, itemFields));
    }
    if (items?.items) {
        // The record's item connection, mapped exactly as FormDrawer mapped it before.
        return {
            ...items,
            items: items.items.map((item: SubscriptionItem) => ({
                id: item.id || '',
                subscriptionId: subscriptionId || '',
                priceId: item.priceId,
                quantity: Number(item.quantity),
            })),
        };
    }
    return items;
};

export const buildCreateCustomerSubscriptionInput = (values: CustomerSubscription) => ({
    customerId: values.customerId,
    items: buildItems(values.items as FormItems, values.id, CREATE_ITEM_FIELDS),
    status: values.status,
});

export const buildUpdateCustomerSubscriptionInput = (id: string, values: CustomerSubscription) => ({
    id,
    customerId: values.customerId,
    items: buildItems(values.items as FormItems, values.id, UPDATE_ITEM_FIELDS),
    status: values.status,
});
